import React, {useEffect, useState} from 'react';
import {
  IFdFarmAssetType,
  IFdFarmAssetList,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {
  reduxToggleSidePanel,
  reduxSetSidePanelEditState,
} from 'appredux/actions';
import {
  apiGetFdAssets,
  apiUpsertFarmAsset,
  apiDeleteFarmAsset,
} from 'constants/Api';
import {Box} from '@material-ui/core';
import {
  FdFarmAssetForm,
  Submission,
} from 'components/applications/FdFarmAssetForm';
import {Skeleton} from '@material-ui/lab';
import {EAssetCondition} from 'constants/index';

const AddOrUpdateFdAsset = (props: {
  user: ISupplierUserProfile;
  asset?: IFdFarmAssetType;
  applicationId: number;
  farmId: number;
  onAssetUpserted?: () => void;
}) => {
  const [allAssets, setAllAssets] = useState<IFdFarmAssetList[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSuccessfullyUpdated, setHasSuccessfullyUpdated] = useState(false);

  const isAdding = !props.asset?.id;

  useEffect(() => {
    // signal edit started for ApplicationDetail to react
    reduxSetSidePanelEditState('editing');
    apiGetFdAssets()
      .then((assets) => setAllAssets(assets))
      .catch((err) => {
        alert('Failed to load asset list. Please try again later.');
        console.error('Failed to load asset list', err);
      });
    return () => {
      // reset when panel unmounts if not saved
      reduxSetSidePanelEditState('idle');
    };
  }, []);

  const handleDelete = () => {
    if (!props.asset?.id) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${props.asset.description}"? This action cannot be undone.`,
    );

    if (!confirmDelete) return;

    setIsLoading(true);
    apiDeleteFarmAsset(parseInt(props.asset.id))
      .then((success) => {
        if (success) {
          setHasSuccessfullyUpdated(true);
          // mark as saved
          reduxSetSidePanelEditState('saved');
        } else {
          throw new Error('Delete operation returned false');
        }
      })
      .catch((err) => {
        console.error('Failed to delete asset', err);
        const errorMessage =
          err?.message || err?.toString() || 'Unknown error occurred';
        alert(`Failed to delete asset: ${errorMessage}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCancel = () => {
    reduxToggleSidePanel(false, null);
  };

  const handleSubmit = (submission: Submission) => {
    setIsLoading(true);
    apiUpsertFarmAsset({
      id: props.asset?.id ? parseInt(props.asset?.id) : undefined,
      description: submission.description,
      year: submission.year?.toString(),
      condition: submission.condition as EAssetCondition,
      value: submission.value,
      pricePerUnit: submission.pricePerUnit,
      farmId: props.asset?.farm?.id
        ? parseInt(props.asset.farm.id)
        : props.farmId,
      capacity: submission.capacity,
      model: submission.model,
      make: submission.make,
      assetType: submission.assetType,
      assetListId: parseInt(submission.assetListId!),
      applicationId: props.applicationId,
    })
      .then(() => {
        setHasSuccessfullyUpdated(true);
        // mark as saved
        reduxSetSidePanelEditState('saved');
      })
      .catch((err) => {
        alert('Failed to update asset at the moment. Please try again later');
        console.error('Failed to update asset', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const canWrite = true;
  // Close + refresh via side-effect after success
  useEffect(() => {
    if (hasSuccessfullyUpdated) {
      props.onAssetUpserted?.();
      reduxToggleSidePanel(false, null);
    }
  }, [hasSuccessfullyUpdated]);

  if (hasSuccessfullyUpdated) return null;

  if (!allAssets.length) {
    return (
      <Box margin={2}>
        <Skeleton variant="text" height={100} />
      </Box>
    );
  }

  return (
    <FdFarmAssetForm
      asset={props.asset}
      allAssets={allAssets}
      isLoading={isLoading}
      submitButtonTitle={isAdding ? 'Add' : 'Update'}
      onSubmit={handleSubmit}
      cancelButtonTitle={'Cancel'}
      onCancel={handleCancel}
      readonly={!canWrite}
      onDelete={props.asset?.id ? handleDelete : undefined}
    />
  );
};

export default AddOrUpdateFdAsset;
